// Program offering helpers shared by the student portal school page.
// Ported from transition_uni_listings/utils/programDirectory.js so the portal's
// program cards show the same admission facts as transitionu.health.

export function numericFact(value) {
  if (value == null || String(value).trim() === '') return null;
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : null;
}

export function upcomingDeadline(offering = {}, now = Date.now()) {
  if (!offering.application_deadline || !offering.dates_verified_at) return null;
  const verified = Date.parse(offering.dates_verified_at);
  const age = (now - verified) / 86400000;
  if (!(age >= 0 && age <= 365)) return null;
  const value = String(offering.application_deadline).slice(0, 10);
  const deadline = Date.parse(`${value}T23:59:59Z`);
  return Number.isFinite(deadline) && deadline >= now ? deadline : null;
}

const FORMAT_LABELS = { online: 'Online', in_person: 'In person', hybrid: 'Hybrid' };

export function programCardDetails(offering = {}) {
  const details = [];
  const tuition = numericFact(offering.tuition_total);
  const months = numericFact(offering.duration_months);
  const pass = numericFact(offering.licensure_pass_rate);
  if (tuition !== null) details.push({ label: 'Tuition', value: `$${tuition.toLocaleString('en-US')}` });
  if (pass !== null && pass <= 100) {
    details.push({
      label: `Licensure pass rate${offering.pass_rate_year ? ` (${offering.pass_rate_year})` : ''}`,
      value: `${pass}%`,
    });
  }
  if (months !== null && months > 0) details.push({ label: 'Duration', value: `${months} months` });
  if (offering.format) details.push({ label: 'Format', value: FORMAT_LABELS[offering.format] || offering.format });
  if (offering.accreditor) details.push({ label: 'Accreditor', value: offering.accreditor });
  return details;
}

export function programAdmission(offering = {}, now = Date.now()) {
  const deadline = upcomingDeadline(offering, now);
  if (deadline !== null) {
    return {
      label: 'Application deadline',
      value: new Date(deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }),
    };
  }
  const age = (now - Date.parse(offering.dates_verified_at)) / 86400000;
  if (offering.admission_cycle === 'rolling' && age >= 0 && age <= 365) {
    return { label: 'Applications', value: 'Rolling admissions' };
  }
  const start = nextStart(offering, now);
  if (start) return { label: 'Next start', value: start };
  return { label: 'Application deadline', value: 'Confirm with school' };
}

const TERM_LABELS = { fall: 'Fall', spring: 'Spring', summer: 'Summer', winter: 'Winter' };

// Next class start for programs that publish start dates but no deadline;
// falls back to the start terms list (e.g. "Fall, Spring").
export function nextStart(offering = {}, now = Date.now()) {
  if (offering.next_start_date) {
    const value = String(offering.next_start_date).slice(0, 10);
    const start = Date.parse(`${value}T23:59:59Z`);
    if (Number.isFinite(start) && start >= now) {
      return new Date(start).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
    }
  }
  const terms = Array.isArray(offering.start_terms) ? offering.start_terms.map(t => TERM_LABELS[t] || t).filter(Boolean) : [];
  return terms.length ? terms.join(', ') : null;
}

export const TU_PUBLIC_URL = (process.env.VUE_APP_TU_URL || 'https://transitionu.health').replace(/\/$/, '');

const normalize = value =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

/** Rows are { key, program, school, offering }. Mirrors utils/programDirectory.js on the public site. */
export function filterProgramEntries(entries, filters, now = Date.now()) {
  const words = normalize(filters.search).split(/\s+/).filter(Boolean);
  const rows = entries.filter(row => {
    const s = row.school;
    return (
      (!filters.program || String(row.program.id) === String(filters.program)) &&
      (!filters.state || s.state === filters.state) &&
      (!filters.city || s.city === filters.city) &&
      (!filters.type || s.institution_type === filters.type) &&
      (!filters.deadline || upcomingDeadline(row.offering, now) !== null) &&
      words.every(word => normalize([row.program.title, row.program.abbr, s.name, s.city, s.state].join(' ')).includes(word))
    );
  });
  const byName = (a, b) =>
    String(a.school.name || '').localeCompare(String(b.school.name || '')) ||
    String(a.program.title || '').localeCompare(String(b.program.title || '')) ||
    a.key.localeCompare(b.key);
  const numericSort = (a, b, key, descending = false) => {
    const av = key === 'deadline' ? upcomingDeadline(a.offering, now) : numericFact(a.offering[key]);
    const bv = key === 'deadline' ? upcomingDeadline(b.offering, now) : numericFact(b.offering[key]);
    if (av === null || bv === null) return av === bv ? byName(a, b) : av === null ? 1 : -1;
    return (descending ? bv - av : av - bv) || byName(a, b);
  };
  const sorters = {
    name: byName,
    state: (a, b) => String(a.school.state || '').localeCompare(String(b.school.state || '')) || byName(a, b),
    tuition: (a, b) => numericSort(a, b, 'tuition_total'),
    pass: (a, b) => numericSort(a, b, 'licensure_pass_rate', true),
    deadline: (a, b) => numericSort(a, b, 'deadline'),
  };
  return rows.sort(sorters[filters.sort] || byName);
}

/** Follow every API page for each program so filters and sorting cover the whole directory. */
export async function loadProgramDirectory(fetchPage, programs) {
  const PAGE_SIZE = 300;
  const entries = [];
  const failed = [];
  let next = 0;
  async function worker() {
    while (next < programs.length) {
      const program = programs[next++];
      try {
        const rows = [];
        const seen = new Set();
        let offset = 0;
        for (;;) {
          const payload = await fetchPage({ program_id: program.id, basic: true, ordering: 'name', limit: PAGE_SIZE, offset });
          const batch = Array.isArray(payload) ? payload : payload && payload.results;
          if (!Array.isArray(batch)) throw new Error('Unexpected program response');
          let added = 0;
          for (const school of batch) {
            if (!school.slug) continue;
            const key = `${program.id}:${school.id || school.slug}`;
            if (seen.has(key)) continue;
            seen.add(key);
            rows.push({ key, program, school, offering: { ...(school.program_offering || {}), program } });
            added++;
          }
          offset += batch.length;
          const total = !Array.isArray(payload) && payload.count != null ? Number(payload.count) : null;
          const more = total != null ? offset < total : batch.length === PAGE_SIZE;
          if (!more) break;
          if (!batch.length || !added) throw new Error('Program pagination did not advance');
        }
        entries.push(...rows);
      } catch (e) {
        failed.push(program);
      }
    }
  }
  await Promise.all(Array.from({ length: Math.min(3, programs.length) }, worker));
  return { entries, failed };
}
